import {
    Box,
    Flex,
    HStack,
    Icon,
    IconButton,
    Spinner,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { TbRefresh, TbRotateClockwise, TbZoomIn, TbZoomOut } from 'react-icons/tb';
import { getDocument } from 'pdfjs-dist';
import type { ZoomImageHandle, ZoomImageHighlightBox } from './ZoomImage';
import '../utils/configurePdfjsWorker';

/** Space around the page inside the scroll area so borders are not flush with the viewport when panning. */
const PDF_EDGE_SCROLL_PADDING_PX = 28;

/**
 * Applied when rendering PDF pages to canvas (display + download). Keeps mean RGB below the
 * upload pipeline's ~220 "too bright" threshold for typical white-heavy scans.
 */
const INVOICE_PDF_CANVAS_BRIGHTNESS = 0.86;

/** Raster scale for "Download PDF" export (display uses fit-to-panel scale). */
const PDF_EXPORT_RASTER_SCALE = 2;

type NormalizedBox = { left: number; top: number; width: number; height: number };

/** Map a 0–1 highlight box into user-rotated page space (clockwise). */
function rotateNormalizedBox(box: NormalizedBox, rotation: number): NormalizedBox {
    const r = ((rotation % 360) + 360) % 360;
    if (r === 90) {
        return {
            left: 1 - box.top - box.height,
            top: box.left,
            width: box.height,
            height: box.width,
        };
    }
    if (r === 180) {
        return {
            left: 1 - box.left - box.width,
            top: 1 - box.top - box.height,
            width: box.width,
            height: box.height,
        };
    }
    if (r === 270) {
        return {
            left: box.top,
            top: 1 - box.left - box.width,
            width: box.height,
            height: box.width,
        };
    }
    return box;
}

interface PdfZoomImageProps {
    src: string;
    pageNumber: number;
    onPageCountChange?: (count: number) => void;
    onPageClamp?: (page: number) => void;
    hideControls?: boolean;
    imageHighlight?: ZoomImageHighlightBox | null;
    imageHighlightFullWidth?: boolean;
}

const PdfZoomImage = forwardRef<ZoomImageHandle, PdfZoomImageProps>(({
    src,
    pageNumber,
    onPageCountChange,
    onPageClamp,
    hideControls = false,
    imageHighlight,
    imageHighlightFullWidth = false,
}, ref) => {
    const [pdfDoc, setPdfDoc] = useState<any | null>(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [rotation, setRotation] = useState(0);
    /** Built-in /Rotate from the PDF page (combined with user rotation for highlights). */
    const [pageRotate, setPageRotate] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [isRendering, setIsRendering] = useState(false);
    const [containerSize, setContainerSize] = useState({ width: 1, height: 1 });
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const pdfDocRef = useRef<any>(null);
    const dragOriginRef = useRef({ clientX: 0, clientY: 0, scrollLeft: 0, scrollTop: 0 });
    const dragActiveRef = useRef(false);

    useEffect(() => {
        pdfDocRef.current = pdfDoc;
    }, [pdfDoc]);

    const scrollToCenter = useCallback(() => {
        const el = scrollAreaRef.current;
        if (!el) return;
        requestAnimationFrame(() => {
            el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
            el.scrollTop = Math.max(0, (el.scrollHeight - el.clientHeight) / 2);
        });
    }, []);

    const downloadToneAdjustedPdf = useCallback(async (filenameBase: string) => {
        const doc = pdfDocRef.current;
        if (!doc?.numPages) {
            throw new Error('PDF is not loaded yet.');
        }
        const safeBase = filenameBase.replace(/[/\\?%*:|"<>]/g, '-').trim() || 'invoice';
        const name = safeBase.toLowerCase().endsWith('.pdf') ? safeBase : `${safeBase}.pdf`;

        const exportCanvas = document.createElement('canvas');
        const exportCtx = exportCanvas.getContext('2d');
        if (!exportCtx) return;

        const { jsPDF } = await import('jspdf');
        let pdfOut: InstanceType<typeof jsPDF> | null = null;
        for (let p = 1; p <= doc.numPages; p++) {
            const page = await doc.getPage(p);
            const vp = page.getViewport({ scale: PDF_EXPORT_RASTER_SCALE });
            const w = Math.floor(vp.width);
            const h = Math.floor(vp.height);
            exportCanvas.width = w;
            exportCanvas.height = h;
            exportCtx.setTransform(1, 0, 0, 1, 0, 0);
            exportCtx.fillStyle = '#ffffff';
            exportCtx.fillRect(0, 0, w, h);
            exportCtx.filter = `brightness(${INVOICE_PDF_CANVAS_BRIGHTNESS})`;
            const task = page.render({
                canvasContext: exportCtx,
                viewport: vp,
            });
            await (task as any).promise;
            exportCtx.filter = 'none';

            const imgData = exportCanvas.toDataURL('image/jpeg', 0.88);
            if (!pdfOut) {
                pdfOut = new jsPDF({ unit: 'pt', format: [w, h], compress: true });
                pdfOut.addImage(imgData, 'JPEG', 0, 0, w, h);
            } else {
                pdfOut.addPage([w, h]);
                pdfOut.addImage(imgData, 'JPEG', 0, 0, w, h);
            }
        }
        if (pdfOut) pdfOut.save(name);
    }, []);

    const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!e.isPrimary || e.button !== 0) return;
        const el = scrollAreaRef.current;
        if (!el) return;
        e.preventDefault();
        dragOriginRef.current = {
            clientX: e.clientX,
            clientY: e.clientY,
            scrollLeft: el.scrollLeft,
            scrollTop: el.scrollTop,
        };
        dragActiveRef.current = true;
        setIsDragging(true);
        el.setPointerCapture(e.pointerId);
    }, []);

    const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragActiveRef.current) return;
        const el = scrollAreaRef.current;
        if (!el) return;
        el.scrollLeft = dragOriginRef.current.scrollLeft - (e.clientX - dragOriginRef.current.clientX);
        el.scrollTop = dragOriginRef.current.scrollTop - (e.clientY - dragOriginRef.current.clientY);
    }, []);

    const totalRotation = useMemo(
        () => (pageRotate + rotation) % 360,
        [pageRotate, rotation],
    );

    const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragActiveRef.current) return;
        dragActiveRef.current = false;
        setIsDragging(false);
        const el = scrollAreaRef.current;
        if (el) {
            try {
                if (el.hasPointerCapture(e.pointerId)) {
                    el.releasePointerCapture(e.pointerId);
                }
            } catch {
                /* ignore */
            }
        }
    }, []);

    const handlePointerCancel = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragActiveRef.current) return;
        dragActiveRef.current = false;
        setIsDragging(false);
        const el = scrollAreaRef.current;
        if (el) {
            try {
                if (el.hasPointerCapture(e.pointerId)) {
                    el.releasePointerCapture(e.pointerId);
                }
            } catch {
                /* ignore */
            }
        }
    }, []);

    const handleLostPointerCapture = useCallback(() => {
        dragActiveRef.current = false;
        setIsDragging(false);
    }, []);

    useImperativeHandle(ref, () => ({
        zoomIn: () => setZoomLevel((prev) => Math.min(prev + 0.5, 4)),
        zoomOut: () => setZoomLevel((prev) => Math.max(prev - 0.5, 0.5)),
        reset: () => {
            setZoomLevel(1);
            setRotation(0);
            scrollToCenter();
        },
        rotate: () => setRotation((prev) => (prev + 90) % 360),
        get zoomLevel() {
            return zoomLevel;
        },
        downloadToneAdjustedPdf,
    }), [zoomLevel, scrollToCenter, downloadToneAdjustedPdf]);

    useEffect(() => {
        setRotation(0);
        setPageRotate(0);
    }, [src, pageNumber]);

    /** Recenter when zoom/page changes or canvas is refit (e.g. resize). */
    useEffect(() => {
        if (canvasSize.width === 0) return;
        scrollToCenter();
    }, [zoomLevel, pageNumber, rotation, canvasSize.width, canvasSize.height, scrollToCenter]);

    useEffect(() => {
        let cancelled = false;
        setError(null);
        setPdfDoc(null);

        const load = async () => {
            try {
                const loadingTask = getDocument(src);
                const doc = await loadingTask.promise;
                if (cancelled) return;
                setPdfDoc(doc);
                onPageCountChange?.(doc.numPages);
                if (pageNumber > doc.numPages) onPageClamp?.(doc.numPages);
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : 'Failed to load PDF');
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [src, onPageCountChange, onPageClamp, pageNumber]);

    useEffect(() => {
        if (!containerRef.current) return;
        const node = containerRef.current;
        const observer = new ResizeObserver(() => {
            const rect = node.getBoundingClientRect();
            setContainerSize({
                width: Math.max(1, Math.floor(rect.width)),
                height: Math.max(1, Math.floor(rect.height)),
            });
        });
        observer.observe(node);
        const rect = node.getBoundingClientRect();
        setContainerSize({
            width: Math.max(1, Math.floor(rect.width)),
            height: Math.max(1, Math.floor(rect.height)),
        });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        let cancelled = false;
        let renderTask: { cancel: () => void } | null = null;

        const render = async () => {
            if (!pdfDoc || !canvasRef.current) return;
            setIsRendering(true);
            try {
                const safePage = Math.min(Math.max(pageNumber, 1), pdfDoc.numPages);
                const page = await pdfDoc.getPage(safePage);
                if (cancelled) return;
                const intrinsicRotate = page.rotate || 0;
                setPageRotate(intrinsicRotate);
                const totalRotate = (intrinsicRotate + rotation) % 360;
                const viewport = page.getViewport({ scale: 1, rotation: totalRotate });
                const fitInset = 12 + 2 * PDF_EDGE_SCROLL_PADDING_PX;
                const fitScale = Math.min(
                    (containerSize.width - fitInset) / viewport.width,
                    (containerSize.height - fitInset) / viewport.height
                );
                const scale = Math.max(0.1, fitScale) * zoomLevel;
                const scaledViewport = page.getViewport({ scale, rotation: totalRotate });

                const canvas = canvasRef.current;
                const ctx = canvas.getContext('2d');
                if (!ctx) return;

                canvas.width = Math.floor(scaledViewport.width);
                canvas.height = Math.floor(scaledViewport.height);
                setCanvasSize({ width: canvas.width, height: canvas.height });

                try {
                    ctx.filter = `brightness(${INVOICE_PDF_CANVAS_BRIGHTNESS})`;
                    renderTask = page.render({
                        canvasContext: ctx,
                        viewport: scaledViewport,
                    });
                    await (renderTask as any).promise;
                } finally {
                    ctx.filter = 'none';
                }
            } catch (err: unknown) {
                if (cancelled) return;
                if (!(err instanceof Error && err.name === 'RenderingCancelledException')) {
                    setError(err instanceof Error ? err.message : 'Failed to render PDF page');
                }
            } finally {
                if (!cancelled) setIsRendering(false);
            }
        };

        render();
        return () => {
            cancelled = true;
            renderTask?.cancel();
        };
    }, [pdfDoc, pageNumber, zoomLevel, rotation, containerSize]);

    const highlightStyle = useMemo(() => {
        if (!imageHighlight || canvasSize.width <= 0 || canvasSize.height <= 0) return null;
        const rotated = rotateNormalizedBox(imageHighlight, totalRotation);
        const r = ((totalRotation % 360) + 360) % 360;
        const spanOriginalWidth = imageHighlightFullWidth && (r === 0 || r === 180);
        const spanOriginalHeight = imageHighlightFullWidth && (r === 90 || r === 270);
        return {
            left: spanOriginalWidth ? 0 : rotated.left * canvasSize.width,
            top: spanOriginalHeight ? 0 : rotated.top * canvasSize.height,
            width: spanOriginalWidth ? canvasSize.width : rotated.width * canvasSize.width,
            height: spanOriginalHeight ? canvasSize.height : rotated.height * canvasSize.height,
        };
    }, [imageHighlight, imageHighlightFullWidth, canvasSize.height, canvasSize.width, totalRotation]);

    if (error) {
        return (
            <Flex h="100%" align="center" justify="center">
                <Text color="red.500" fontSize="sm">{error}</Text>
            </Flex>
        );
    }

    return (
        <Box ref={containerRef} position="relative" w="100%" h="100%">
            {!hideControls && (
                <HStack
                    position="absolute"
                    top={2}
                    right={2}
                    zIndex={10}
                    spacing={1}
                    bg="white"
                    borderRadius="md"
                    p={1}
                    boxShadow="none"
                >
                    <Tooltip label="Zoom In">
                        <IconButton
                            aria-label="Zoom In"
                            icon={<Icon as={TbZoomIn as React.ElementType} w={4} h={4} />}
                            size="sm"
                            variant="ghost"
                            onClick={() => setZoomLevel((prev) => Math.min(prev + 0.5, 4))}
                            isDisabled={zoomLevel >= 4}
                        />
                    </Tooltip>
                    <Tooltip label="Zoom Out">
                        <IconButton
                            aria-label="Zoom Out"
                            icon={<Icon as={TbZoomOut as React.ElementType} w={4} h={4} />}
                            size="sm"
                            variant="ghost"
                            onClick={() => setZoomLevel((prev) => Math.max(prev - 0.5, 0.5))}
                            isDisabled={zoomLevel <= 0.5}
                        />
                    </Tooltip>
                    <Tooltip label="Reset View">
                        <IconButton
                            aria-label="Reset View"
                            icon={<Icon as={TbRefresh as React.ElementType} w={4} h={4} />}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                                setZoomLevel(1);
                                setRotation(0);
                                scrollToCenter();
                            }}
                        />
                    </Tooltip>
                    <Tooltip label="Rotate 90°">
                        <IconButton
                            aria-label="Rotate 90°"
                            icon={<Icon as={TbRotateClockwise as React.ElementType} w={4} h={4} />}
                            size="sm"
                            variant="ghost"
                            onClick={() => setRotation((prev) => (prev + 90) % 360)}
                        />
                    </Tooltip>
                </HStack>
            )}
            <Box
                ref={scrollAreaRef}
                h="100%"
                w="100%"
                overflow="auto"
                p={2}
                cursor={isDragging ? 'grabbing' : 'grab'}
                userSelect="none"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                onLostPointerCapture={handleLostPointerCapture}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minW="100%"
                    minH="100%"
                    w="max-content"
                    h="max-content"
                    boxSizing="border-box"
                >
                    <Box flexShrink={0} p={`${PDF_EDGE_SCROLL_PADDING_PX}px`}>
                        <Box position="relative" lineHeight={0}>
                            <canvas ref={canvasRef} style={{ display: 'block' }} />
                            {highlightStyle && (
                                <Box
                                    pointerEvents="none"
                                    position="absolute"
                                    left={`${highlightStyle.left}px`}
                                    top={`${highlightStyle.top}px`}
                                    w={`${highlightStyle.width}px`}
                                    h={`${highlightStyle.height}px`}
                                    bg="rgba(255, 235, 59, 0.32)"
                                    boxShadow="inset 0 1px 0 rgba(255, 235, 59, 0.6), inset 0 -1px 0 rgba(255, 235, 59, 0.6)"
                                />
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
            {isRendering && (
                <Flex position="absolute" inset={0} align="center" justify="center" pointerEvents="none">
                    <Spinner size="sm" color="blue.500" />
                </Flex>
            )}
        </Box>
    );
});

PdfZoomImage.displayName = 'PdfZoomImage';

export default PdfZoomImage;
